import React, { ReactElement } from "react";

import { faArrowDown, faArrowUp } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import ReactAvatar from "react-avatar";
import { NumericFormat } from "react-number-format";

interface StatItemProps {
	label: string;
	value: number;
	previousValue: number;
	isCurrency?: boolean;
	icon: string;
	color: string;
}

const StatItem = ({
	label,
	value,
	previousValue,
	isCurrency,
	icon,
	color,
}: StatItemProps): ReactElement => {
	const isUp = value >= previousValue && value !== 0;
	const percentChange =
		previousValue > 0
			? Math.round(((value - previousValue) / previousValue) * 100)
			: value > 0
			? 100
			: 0;

	return (
		<div className="stat-item">
			<div className="stat-icon" style={{ background: color }}>
				<i className={`fa ${icon}`} />
			</div>
			<div className="stat-content">
				<span className="stat-label">{label}</span>
				<div className="stat-value-row">
					<span className="stat-value">
						{isCurrency ? (
							<NumericFormat
								value={value.toFixed(2)}
								displayType="text"
								thousandSeparator={true}
								prefix="$"
							/>
						) : (
							value
						)}
					</span>
					<span
						className={`stat-trend ${
							isUp ? "trend-up" : "trend-down"
						}`}
					>
						<FontAwesomeIcon
							icon={isUp ? faArrowUp : faArrowDown}
						/>
						<span>{Math.abs(percentChange)}%</span>
					</span>
				</div>
			</div>
		</div>
	);
};

interface UserStatsTableRowProps {
	stats: any;
	isTopPerformer?: boolean;
	rank?: number;
}

export const UserStatsTableRow = ({
	stats,
	isTopPerformer,
	rank,
}: UserStatsTableRowProps): ReactElement => {
	return (
		<div
			className={`user-stat-card ${
				isTopPerformer ? "top-performer" : ""
			}`}
		>
			<div className="user-stat-header">
				{isTopPerformer && (
					<div className="top-performer-badge">
						<span className="crown">👑</span>
					</div>
				)}
				{rank && !isTopPerformer && (
					<div className="rank-badge">#{rank}</div>
				)}
				<ReactAvatar
					name={stats.name}
					color={isTopPerformer ? "#f59e0b" : "#6366f1"}
					fgColor="#fff"
					size="42px"
					round
				/>
				<span className="user-name">{stats.name}</span>
			</div>
			<div className="user-stat-metrics">
				<StatItem
					label="Leads"
					value={stats.leads_this_month}
					previousValue={stats.leads_last_month}
					icon="fa-user-plus"
					color="#10b981"
				/>
				<StatItem
					label="Calls"
					value={stats.calls_this_month}
					previousValue={stats.calls_last_month}
					icon="fa-phone"
					color="#3b82f6"
				/>
				<StatItem
					label="Activities"
					value={stats.activities_this_month}
					previousValue={stats.activities_last_month}
					icon="fa-tasks"
					color="#8b5cf6"
				/>
				<StatItem
					label="Follow-Ups"
					value={stats.csfu_this_month}
					previousValue={stats.csfu_last_month}
					icon="fa-calendar-check"
					color="#f59e0b"
				/>
				<StatItem
					label="Sales"
					value={stats.sales_this_month}
					previousValue={stats.sales_last_month}
					isCurrency={true}
					icon="fa-dollar"
					color="#ef4444"
				/>
			</div>
		</div>
	);
};
